<template>
  <section class="advent-reward">
    <div class="advent-reward-current flexing">
      <div class="advent-reward-current-text">
        <div class="advent-reward-current-text-header">{{ $t('event.current_day') }}:</div>

        <div v-if="showTimer" class="advent-reward-current-text-timer flexing">
          <div class="advent-reward-current-text-timer-next">{{ $t('event.reward_unlock') }}:</div>
          <app-timer :time="timeToNextDay" transparent />
        </div>
      </div>

      <div class="advent-reward-current-day flexing">
        <div class="advent-reward-current-day-number" :class="'number-' + currentWindow" />
      </div>
    </div>

    <div class="advent-reward-detail" :class="selectedWindow?.reward ? 'opened' : ''">
      <div class="advent-reward-detail-header w-full">
        <div v-if="selectedWindow" class="advent-reward-detail-header-selected">
          {{ $t('event.selected') }}
        </div>

        <div v-if="selectedWindow" class="advent-reward-detail-header-day">
          {{ $replacePlaceholder($t('event.day'), '%s', selectedWindow.order) }}
        </div>
      </div>

      <div class="advent-reward-detail-window">
        <div v-if="isMissed && showRewards" class="advent-reward-detail-window-missed">
          {{ $t('event.missed_reward') }}
        </div>

        <calendar-window
          v-if="showRewards"
          :calendar-window="selectedWindow"
          :is-interactive="false"
        />

        <div v-else class="advent-reward-detail-window-mystery" />
      </div>

      <watch-for-reward
        v-if="showWatchBtn"
        ad-type="advent_calendar"
        :advent-day="selectedWindow.order"
        :btn-text="watchBtnText"
        centered
        @reload-content="afterVideo(selectedWindow.order)"
      />

      <div v-if="showWatchBtn" class="advent-reward-arrow" />

      <div v-if="hasWatchedVideo || isClaimed" class="advent-reward-bonustext">
        <span v-if="hasWatchedVideo">{{ $t('event.reward_with_bonus') }}</span>
        <span v-else-if="isClaimed">{{ $t('event.base_reward') }}</span>
      </div>

      <!-- claim missed - already watched video -->
      <app-multi-button1
        v-if="canClaim && isMissed && hasWatchedVideo"
        btn-type="credit"
        :btn-text="$t('event.claimReward')"
        :btn-count="missedCost"
        :popup-data="
          $filters.$confirmPopupData({
            title: $t('event.claimReward'),
            btnId: 'btn-claim',
            btnText: $t('event.claimReward'),
            btnValue: missedCost,
          })
        "
        class="advent-reward-btn claim-single-btn"
        @action="claimReward"
      >
        <template #popupSlot>
          <div class="container-upgrade text-36 text-texts-standard-default">
            <p>
              {{ $t('event.confirm_claim_gems') }}
            </p>
            <p class="text-texts-standard-important">
              <strong>{{ $t('event.price') }}: {{ missedCost }}</strong>
              <app-main-icon icon-name="gems" :icon-size="52" />
            </p>
          </div>
        </template>
      </app-multi-button1>

      <!-- claim missed - not watched video -->
      <app-multi-button1
        v-else-if="canClaim && isMissed"
        btn-type="credit"
        :btn-text="$t('event.confirm_claim_header')"
        :btn-count="missedCost"
        :popup-data="
          $filters.$confirmPopupData({
            title: $t('event.confirm_claim__title'),
            btnId: 'btn-claim',
            btnText: $t('event.confirm_claim_header'),
            btnValue: missedCost,
          })
        "
        class="advent-reward-btn"
        @action="claimConfirmed"
      >
        <template #popupSlot>
          <div class="container-upgrade text-36 text-texts-standard-default">
            <br />

            <p>
              {{ $t('event.confirm_claim_text') }}
            </p>

            <p class="text-texts-standard-important">
              {{ $t('event.confirm_claim_text_2') }}
            </p>

            <br />

            <watch-for-reward
              ad-type="advent_calendar"
              :advent-day="selectedWindow.order"
              :btn-text="watchBtnText"
              centered
              @reload-content="afterVideo(selectedWindow.order)"
            />

            <br />

            <div class="flex">
              <app-icon icon-name="info-rounded" />
              <span class="pl-4">{{ $t('event.confirm_claim_text_3') }}</span>
            </div>
          </div>
        </template>
      </app-multi-button1>

      <!-- claim current (free) -->
      <app-button
        v-else-if="canClaim"
        class="advent-reward-btn"
        :class="{ 'claim-single-btn': hasWatchedVideo }"
        btn-type="primary"
        :btn-text="hasWatchedVideo ? $t('event.claim_reward') : $t('event.confirm_claim_header')"
        btn-size="md"
        @click="handleClaimCurrent"
      />
      <confirm-popup
        v-if="showConfirm"
        :button-data="buttonData"
        :popup-title="$t('event.confirm_claim_header')"
        :single-button-text="$t('event.confirm_claim_header')"
        custom-width="64.375rem"
        not-multi
        @close="closeConfirm"
        @action="claimConfirmed"
      >
        <div class="container-upgrade text-36 text-texts-standard-default italic">
          <br />

          <p>
            {{ $t('event.confirm_claim_text') }}
          </p>

          <p class="text-texts-standard-important">
            {{ $t('event.confirm_claim_text_2') }}
          </p>

          <br />

          <watch-for-reward
            ad-type="advent_calendar"
            :advent-day="selectedWindow.order"
            :btn-text="watchBtnText"
            centered
            @reload-content="afterVideo(selectedWindow.order)"
          />

          <br />

          <div class="flex">
            <app-icon icon-name="info-rounded" />
            <span class="pl-4">{{ $t('event.confirm_claim_text_3') }}</span>
          </div>

          <br />
        </div>
      </confirm-popup>

      <div v-if="isClaimed" class="advent-reward-claimed">
        {{ $t('event.claimed') }}
      </div>

      <div v-if="!selectedWindow" class="advent-reward-detail-click">
        {{ $t('event.click_to_reveal') }}
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useAdventCalendarStore } from '@/store/pinia/events/adventCalendarStore'
import AppButton from '../GlobalComponents/AppButton.vue'
import { WindowStatus } from '@/interfaces/events/AdventCalendar'
import CalendarWindow from '@/components/AdventCalendar/CalendarWindow.vue'
import ConfirmPopup from '@/components/Popup/ConfirmPopup.vue'
import WatchForReward from '@/components/Premium/Accessories/WatchForReward.vue'

interface ComponentData {
  buttonData: {
    btnId: string
    btnType: string
    btnSize: string
  }
  showConfirm: boolean
}

export default defineComponent({
  name: 'RewardDetail',

  components: {
    CalendarWindow,
    AppButton,
    ConfirmPopup,
    WatchForReward,
  },

  data(): ComponentData {
    return {
      buttonData: {
        btnId: 'btn-confirm-claim',
        btnType: 'primary',
        btnSize: 'md',
      },
      showConfirm: false,
    }
  },

  computed: {
    ...mapState(useAdventCalendarStore, [
      'currentWindow',
      'selectedWindow',
      'missedCost',
      'timeToNextDay',
    ]),

    showTimer(): boolean {
      return !!this.timeToNextDay && this.currentWindow !== 24
    },

    watchBtnText(): string {
      return this.selectedWindow.adMultiplier > 2
        ? this.$t('event.triple_reward')
        : this.$t('event.double_reward')
    },

    canClaim(): boolean {
      return (
        !!this.selectedWindow?.reward &&
        (this.selectedWindow?.status === WindowStatus.Active ||
          this.selectedWindow?.status === WindowStatus.Missed)
      )
    },

    isMissed(): boolean {
      return this.selectedWindow?.status === WindowStatus.Missed
    },

    isClaimed(): boolean {
      return this.selectedWindow?.status === WindowStatus.Claimed
    },

    showRewards(): boolean {
      if (!this.selectedWindow) return false
      return this.selectedWindow.status !== WindowStatus.Future
    },

    hasWatchedVideo(): boolean {
      return !!this.selectedWindow?.videoWatched
    },

    showWatchBtn(): boolean {
      return this.canClaim && !this.hasWatchedVideo
    },
  },

  methods: {
    ...mapActions(useAdventCalendarStore, ['claimReward', 'afterVideo']),

    claimConfirmed(): void {
      this.claimReward()
      this.closeConfirm()
    },

    closeConfirm(): void {
      this.showConfirm = false
    },

    handleClaimCurrent(): void {
      if (this.hasWatchedVideo) {
        this.claimReward()
        return
      }

      this.showConfirm = true
    },
  },
})
</script>

<style lang="scss">
@import '@/assets/styles/views/advent-calendar/detail.scss';
</style>
